import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AddOutlined from '@mui/icons-material/AddOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';

import { Z_INDEX } from '@/constants/z-index';
import { normalizeSx } from '@/utils/normalizeSx';

import type { SxProps, Theme } from '@mui/material/styles';

// ============================================================================
// Constants
// ============================================================================

const NEW_DASHBOARD_KEY = '__new_dashboard__';

// ============================================================================
// Types
// ============================================================================

export type TabInfo = {
  tabId: string;
  tabName: string;
};

export type CreateDashboardResult =
  | { ok: true; dashboardName: string }
  | { ok: false; reason?: string; message?: string };

export type DashboardTargetMenuVariant = 'with-tabs' | 'dashboard-only';

export interface DashboardTargetMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;

  /**
   * Variant of the menu:
   * - 'with-tabs': Shows dashboard list with tab submenu on hover (default)
   * - 'dashboard-only': Shows only dashboard list without tab submenu
   */
  variant?: DashboardTargetMenuVariant;

  /**
   * Current tab ID to exclude from the list (when variant is 'with-tabs')
   */
  excludeCurrentTabId?: string | null;

  /**
   * Whether to show "New page" option in the submenu
   */
  showNewPageOption?: boolean;

  /**
   * Whether to show chevron icon on dashboard items
   */
  showChevron?: boolean;

  /**
   * Called when a tab is selected (variant 'with-tabs')
   */
  onSelectTab?: (dashboardName: string, tab: TabInfo) => void;

  /**
   * Called when "New page" is selected (variant 'with-tabs')
   */
  onCreateNewTab?: (dashboardName: string) => void;

  /**
   * Called when a dashboard is selected (variant 'dashboard-only')
   */
  onSelectDashboard?: (dashboardName: string) => void;

  /**
   * Optional filter for tab names (return true to include)
   */
  filterTab?: (dashboardName: string, tabName: string, tabId: string) => boolean;

  /**
   * Optional custom empty state message
   */
  emptyMessage?: string;

  /**
   * Whether to disable all interactions
   */
  disabled?: boolean;

  /**
   * Callback for creating a new dashboard draft.
   * Should call context.createDashboardDraft and return the result.
   */
  onCreateDashboard?: () =>
    | CreateDashboardResult
    | Promise<CreateDashboardResult | null>
    | null
    | void;

  /**
   * List of dashboard names to display.
   */
  dashboards: string[];

  /**
   * Function to get tabs for a dashboard.
   */
  getTabs: (dashboardName: string) => TabInfo[] | null;

  /**
   * Current dashboard name to highlight/sort.
   */
  currentDashboardName?: string | null;

  /**
   * Callback for errors during dashboard creation
   */
  onError?: (message: string) => void;

  /**
   * Optional header text shown above the dashboard list
   */
  headerText?: string;

  /**
   * Optional sx overrides for the Paper container
   */
  paperSx?: SxProps<Theme>;

  /**
   * Optional sx overrides for the main Popper element
   */
  popperSx?: SxProps<Theme>;

  /**
   * Placement of the main Popper (default: 'bottom-start')
   */
  placement?: 'bottom-start' | 'bottom-end' | 'bottom';

  /**
   * [skidding, distance] offset for the main Popper (default: [0, 0])
   */
  offset?: [number, number];

  /**
   * z-index when the dropdown opens below the anchor element (default: 10000).
   * Should be above any element whose shadow would bleed onto the dropdown.
   */
  zIndexWhenBelow?: number;

  /**
   * z-index when the dropdown flips above the anchor element (default: 10000).
   * Should be below any element whose shadow the dropdown would bleed onto.
   */
  zIndexWhenAbove?: number;
}

// ============================================================================
// Styles
// ============================================================================

const MENU_ITEM_HEIGHT = 36;
const MENU_OFFSET: [number, number] = [0, 0];

const SELECTOR_BOX_SHADOW = '0 6px 8px rgba(255,255,255,0.15)';

const paperBaseSx: SxProps<Theme> = {
  backgroundColor: 'secondary.main',
  minWidth: 200,
  boxShadow: SELECTOR_BOX_SHADOW
};

const menuItemStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 1,
  minHeight: MENU_ITEM_HEIGHT,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  }
} as const;

const activeMenuItemStyles = {
  ...menuItemStyles,
  backgroundColor: 'rgba(255, 255, 255, 0.08)'
} as const;

const disabledMenuItemStyles = {
  ...menuItemStyles,
  cursor: 'not-allowed',
  opacity: 0.6,
  '&:hover': {
    backgroundColor: 'inherit'
  }
} as const;

const menuItemTextStyles = {
  fontSize: '12px',
  fontWeight: 600
} as const;

// ============================================================================
// Internal hook — hover state for the dashboard list + submenu
// ============================================================================

function useDashboardSubmenuHover() {
  const [hoveredDashboard, setHoveredDashboard] = useState<string | null>(null);
  const [hoveredDashboardRef, setHoveredDashboardRef] = useState<HTMLElement | null>(null);
  const dashboardItemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const subMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setDashboardRef = useCallback((key: string, el: HTMLElement | null) => {
    if (el) {
      dashboardItemRefs.current.set(key, el);
    } else {
      dashboardItemRefs.current.delete(key);
    }
  }, []);

  const setHovered = useCallback((key: string | null) => {
    setHoveredDashboard(key);
    setHoveredDashboardRef(key ? (dashboardItemRefs.current.get(key) ?? null) : null);
  }, []);

  const resetHover = useCallback(() => {
    if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
    setHovered(null);
  }, [setHovered]);

  const handleDashboardMouseEnter = useCallback(
    (dashboardName: string) => {
      if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
      setHovered(dashboardName);
    },
    [setHovered]
  );

  const handleDashboardMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      setHovered(null);
    }, 150);
  }, [setHovered]);

  const handleSubMenuMouseEnter = useCallback(() => {
    if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
  }, []);

  const handleSubMenuMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      setHovered(null);
    }, 150);
  }, [setHovered]);

  return {
    hoveredDashboard,
    hoveredDashboardRef,
    setDashboardRef,
    handleDashboardMouseEnter,
    handleDashboardMouseLeave,
    handleSubMenuMouseEnter,
    handleSubMenuMouseLeave,
    resetHover
  };
}

// ============================================================================
// Internal component — the right-side tab submenu
// ============================================================================

interface DashboardTabSubmenuProps {
  anchorEl: HTMLElement;
  dashboardName: string;
  tabs: TabInfo[];
  showNewPageOption: boolean;
  disabled: boolean;
  zIndex: number;
  backgroundColor: unknown;
  onTabClick: (dashboardName: string, tabId: string, tabName: string) => void;
  onNewTabClick: (dashboardName: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const DashboardTabSubmenu = ({
  anchorEl,
  dashboardName,
  tabs,
  showNewPageOption,
  disabled,
  zIndex,
  backgroundColor,
  onTabClick,
  onNewTabClick,
  onMouseEnter,
  onMouseLeave
}: DashboardTabSubmenuProps) => (
  <Popper
    key={dashboardName}
    open
    anchorEl={anchorEl}
    modifiers={[
      { name: 'offset', options: { offset: MENU_OFFSET } },
      {
        // Reveal only after Popper.js has written the correct position.
        name: 'revealAfterPositioned',
        enabled: true,
        phase: 'afterWrite',
        fn({ state }: { state: { elements: { popper: HTMLElement } } }) {
          state.elements.popper.style.visibility = 'visible';
        }
      }
    ]}
    placement='right-start'
    sx={{ visibility: 'hidden', zIndex }}
  >
    <Paper
      sx={{
        backgroundColor: backgroundColor ?? 'secondary.main',
        minWidth: 180,
        boxShadow: SELECTOR_BOX_SHADOW
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {tabs.map(({ tabName, tabId }) => (
        <Box
          key={tabId}
          sx={disabled ? disabledMenuItemStyles : menuItemStyles}
          onClick={() => {
            onTabClick(dashboardName, tabId, tabName);
          }}
        >
          <Typography sx={menuItemTextStyles}>{tabName}</Typography>
        </Box>
      ))}
      {showNewPageOption && (
        <Box
          sx={disabled ? disabledMenuItemStyles : menuItemStyles}
          onClick={() => {
            onNewTabClick(dashboardName);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddOutlined sx={{ fontSize: 18 }} />
            <Typography sx={menuItemTextStyles}>New page</Typography>
          </Box>
        </Box>
      )}
    </Paper>
  </Popper>
);

// ============================================================================
// Component
// ============================================================================

export const DashboardTargetMenu = ({
  anchorEl,
  open,
  onClose,
  variant = 'with-tabs',
  excludeCurrentTabId: _excludeCurrentTabId,
  showNewPageOption = true,
  showChevron = true,
  onSelectTab,
  onCreateNewTab,
  onSelectDashboard,
  filterTab: _filterTab,
  emptyMessage = 'No other dashboards',
  disabled = false,
  onCreateDashboard,
  dashboards,
  getTabs,
  currentDashboardName: customCurrentDashboardName,
  onError,
  headerText,
  paperSx,
  popperSx,
  placement = 'bottom-start',
  offset: offsetProp = MENU_OFFSET,
  zIndexWhenBelow = Z_INDEX.panel,
  zIndexWhenAbove = Z_INDEX.panel
}: DashboardTargetMenuProps) => {
  // Extract only backgroundColor from paperSx so the sub-menu inherits the color
  // without also inheriting width/minWidth overrides from the caller.
  const subMenuBg =
    paperSx && !Array.isArray(paperSx) && typeof paperSx === 'object'
      ? (paperSx as { backgroundColor?: unknown }).backgroundColor
      : undefined;

  // Track whether the main Popper has flipped to open above the anchor.
  // Updated by a Popper.js modifier so it's always in sync with the real placement.
  const [isFlippedToTop, setIsFlippedToTop] = useState(false);

  // z-index must be above the selector when the dropdown is below it (so the
  // selector's shadow doesn't bleed onto the dropdown), and below the selector
  // when it flips above (so the dropdown's shadow doesn't bleed onto the selector).
  const effectiveZIndex = isFlippedToTop ? zIndexWhenAbove : zIndexWhenBelow;

  const {
    hoveredDashboard,
    hoveredDashboardRef,
    setDashboardRef,
    handleDashboardMouseEnter,
    handleDashboardMouseLeave,
    handleSubMenuMouseEnter,
    handleSubMenuMouseLeave,
    resetHover
  } = useDashboardSubmenuHover();

  // Reset hover state when the menu is closed to prevent the sub-menu
  // from appearing at (0,0) when the menu is reopened.
  useEffect(() => {
    if (!open) {
      resetHover();
    }
  }, [open, resetHover]);

  const dashboardNamesList = useMemo(
    () =>
      [...dashboards].sort((a, b) => {
        if (variant === 'with-tabs') {
          if (a === customCurrentDashboardName) return -1;
          if (b === customCurrentDashboardName) return 1;
        }
        return a.localeCompare(b);
      }),
    [dashboards, customCurrentDashboardName, variant]
  );

  const hoveredDashboardTabs = useMemo(() => {
    if (!hoveredDashboard || variant === 'dashboard-only') return null;
    const tabs = getTabs(hoveredDashboard);
    if (!tabs && showNewPageOption) return [];
    return tabs;
  }, [hoveredDashboard, variant, getTabs, showNewPageOption]);

  const handleTabClick = useCallback(
    (dashboardName: string, tabId: string, tabName: string) => {
      if (disabled) return;
      onSelectTab?.(dashboardName, { tabId, tabName });
      onClose();
    },
    [disabled, onSelectTab, onClose]
  );

  const handleNewTabClick = useCallback(
    (dashboardName: string) => {
      if (disabled) return;
      onCreateNewTab?.(dashboardName);
      onClose();
    },
    [disabled, onCreateNewTab, onClose]
  );

  const handleDashboardClick = useCallback(
    (dashboardName: string) => {
      if (disabled || variant !== 'dashboard-only') return;
      onSelectDashboard?.(dashboardName);
      onClose();
    },
    [disabled, variant, onSelectDashboard, onClose]
  );

  const handleCreateNewDashboard = useCallback(async () => {
    if (disabled || !onCreateDashboard) return;

    const result = await onCreateDashboard();
    if (!result) return;

    if (!result.ok) {
      onError?.(
        result.message ||
          (result.reason
            ? `Failed to create dashboard: ${result.reason}`
            : 'Failed to create dashboard')
      );
      return;
    }

    const newDashboardName = result.dashboardName;
    if (!newDashboardName) {
      onError?.('Failed to create dashboard: missing name.');
      return;
    }

    if (variant === 'with-tabs') {
      if (!onCreateNewTab) {
        onError?.('Failed to create dashboard: missing page handler.');
        return;
      }
      onCreateNewTab(newDashboardName);
    } else {
      onSelectDashboard?.(newDashboardName);
    }
    onClose();
  }, [disabled, onCreateDashboard, variant, onCreateNewTab, onSelectDashboard, onClose, onError]);

  return (
    <Popper
      anchorEl={anchorEl}
      modifiers={[
        { name: 'offset', options: { offset: offsetProp } },
        {
          name: 'trackPlacement',
          enabled: true,
          phase: 'afterWrite',
          fn({ state }: { state: { placement: string } }) {
            setIsFlippedToTop(state.placement.startsWith('top'));
          }
        }
      ]}
      open={open}
      placement={placement}
      sx={[{ zIndex: effectiveZIndex }, ...normalizeSx(popperSx)]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper sx={[paperBaseSx, ...normalizeSx(paperSx)]}>
          {headerText && (
            <Box
              sx={{
                px: 1,
                py: 0.75,
                borderTop: '1px solid rgba(255,255,255,0.1)',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>{headerText}</Typography>
            </Box>
          )}

          {dashboardNamesList.length === 0 && !onCreateDashboard ? (
            <Box sx={disabledMenuItemStyles}>{emptyMessage}</Box>
          ) : (
            <>
              {dashboardNamesList.map(dashboardName => (
                <Box
                  key={dashboardName}
                  ref={(el: HTMLDivElement | null) => {
                    setDashboardRef(dashboardName, el);
                  }}
                  sx={getDashboardItemStyles(disabled, hoveredDashboard === dashboardName)}
                  onClick={() => {
                    handleDashboardClick(dashboardName);
                  }}
                  onMouseEnter={
                    variant === 'with-tabs'
                      ? () => {
                          handleDashboardMouseEnter(dashboardName);
                        }
                      : undefined
                  }
                  onMouseLeave={variant === 'with-tabs' ? handleDashboardMouseLeave : undefined}
                >
                  <Typography sx={menuItemTextStyles}>{dashboardName}</Typography>
                  {showChevron && variant === 'with-tabs' && (
                    <ChevronRight sx={{ fontSize: 20, opacity: 0.7 }} />
                  )}
                </Box>
              ))}

              {onCreateDashboard && (
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    setDashboardRef(NEW_DASHBOARD_KEY, el);
                  }}
                  sx={disabled ? disabledMenuItemStyles : menuItemStyles}
                  onClick={handleCreateNewDashboard}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddOutlined sx={{ fontSize: 18 }} />
                    <Typography sx={menuItemTextStyles}>New Dashboard</Typography>
                  </Box>
                </Box>
              )}
            </>
          )}

          {variant === 'with-tabs' &&
            hoveredDashboard &&
            hoveredDashboardTabs !== null &&
            hoveredDashboardRef && (
              <DashboardTabSubmenu
                anchorEl={hoveredDashboardRef}
                backgroundColor={subMenuBg}
                dashboardName={hoveredDashboard}
                disabled={disabled}
                showNewPageOption={showNewPageOption}
                tabs={hoveredDashboardTabs}
                zIndex={effectiveZIndex + 1}
                onMouseEnter={handleSubMenuMouseEnter}
                onMouseLeave={handleSubMenuMouseLeave}
                onNewTabClick={handleNewTabClick}
                onTabClick={handleTabClick}
              />
            )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

const getDashboardItemStyles = (disabled: boolean, isHovered: boolean) => {
  if (disabled) return disabledMenuItemStyles;
  if (isHovered) return activeMenuItemStyles;
  return menuItemStyles;
};

export default DashboardTargetMenu;
